import React, { Fragment, useState } from 'react';
import { ClientAnalyticsEventType } from '@/components/analytics/constants';
import { useTrackEventsAnalytics } from '@/components/analytics/hooks/use-track-events-analytics';
import { YellowButton } from '@/components/button/button';
import {
  AttendeeStatus,
  BookingFieldsFragment,
  ExperienceEventFieldsFragment,
  ExperienceFieldsFragment,
  ExperienceMode,
} from '@/generated/graphql';
import { useCurrentUser } from '@/hooks/use-current-user/use-current-user';
import { useRelativeTime } from '@/hooks/use-relative-time';
import { getEventJoinUrl } from '@/utils/getEventJoinUrl';
import styled from '@emotion/styled';
import { useMediaQuery } from '@mui/material';
import { useJoinEvent } from '../hooks/use-join-event/use-join-event';
import { useScheduleWelcomeModalOpen } from '../hooks/use-schedule-welcome-modal-open/use-schedule-welcome-modal-open';
import { ScheduleWelcomeModal } from './schedule-welcome-modal';
import { useAuth0 } from '@/auth';
import Cookies from 'js-cookie';
import ClbcPromptModal from '@/components/clbc-prompt/clbc-prompt';

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-top: 30px;
  width: 100%;
  @media (max-width: 768px) {
    display: none;
  }
`;
const MobileButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

interface BookingButtonActionProps {
  event: ExperienceEventFieldsFragment;
  experience: ExperienceFieldsFragment;
}
const BookingButton = ({ event, experience }: BookingButtonActionProps) => {
  const { setScheduleWelcomeModalOpen } = useScheduleWelcomeModalOpen();
  const mode = experience.mode;
  const isMobile = useMediaQuery('(max-width:768px)');
  const [relativeTime, { isNow, isSoon }] = useRelativeTime(event.start, { endTime: event?.end });
  const { joinEvent } = useJoinEvent();
  const { data: user } = useCurrentUser();
  const track = useTrackEventsAnalytics();
  const [modalOpen, setModalOpen] = useState(false);
  const { isAuthenticated } = useAuth0();

  if (!relativeTime) {
    return null;
  }
  const joinUrl = event?.overrideLink || event?.zoomMeeting?.joinUrl;

  // NEW: Handle capacity if defined on the experience and the experience is an event
  const capacityLimitDefined =
    experience?.maximumNumberOfParticipantsPerEvent &&
    experience.maximumNumberOfParticipantsPerEvent > 0;
  let disableDueCapacity = false;
  let spotsLeft = 0;
  if (capacityLimitDefined) {
    const numberOfAttendees =
      event.attendees?.edges?.filter(
        edge => edge?.node?.role !== 'host' && edge?.node?.status !== AttendeeStatus.Cancelled,
      )?.length || 0;
    spotsLeft = (experience?.maximumNumberOfParticipantsPerEvent ?? 0) - numberOfAttendees;
    disableDueCapacity = spotsLeft <= 0;
  }
  if (disableDueCapacity) {
    const disabledButton = <YellowButton disabled>No more spots left</YellowButton>;
    if (isMobile) return <MobileButtonRow>{disabledButton}</MobileButtonRow>;
    return <ButtonRow>{disabledButton}</ButtonRow>;
  }

  const handleJoin = async (eventId?: string, joinUrl?: string, callJoinEvent: boolean = true) => {
    // Check if the user is not authenticated or missing necessary relationship info.
    /*if (!isAuthenticated || user?.hasRelationshipWithCLBC === undefined) {
      // Check for the clbc cookie.
      const clbcCookie = Cookies.get('clbcResponse');
      if (!clbcCookie) {
        // Prompt the user for additional input (login/signup/etc.)
        setModalOpen(true);
        return; // Exit early until user completes the authentication flow.
      }
    }*/

    // For authenticated users or those with an existing cookie,
    // proceed to join the event.
    if (joinUrl) {
      window.open(joinUrl, '_blank');
    } else if (eventId && callJoinEvent) {
      await joinEvent(eventId);
    }
  };

  const clbcPrompt = (
    <ClbcPromptModal
      open={modalOpen}
      onClose={() => {
        setModalOpen(false);
        // Optionally, you can automatically trigger joinEvent after the response is saved:
        // joinEvent(eventId);
      }}
    />
  );

  const content = (
    <>
      {isNow && (
        <YellowButton
          onClick={async () => {
            if (isAuthenticated) {
              track.fireEvent(ClientAnalyticsEventType.CLICKED_JOIN_EVENT, {});
            } else {
              track.fireEvent(ClientAnalyticsEventType.JOIN_EXPERIENCE_NOT_LOGGED_IN, {
                id: experience.id,
                title: experience.title!,
              });
            }
            await handleJoin(event.id, joinUrl, false);
          }}
        >
          Join the experience
        </YellowButton>
      )}
      {isSoon && (
        <YellowButton
          onClick={async () => {
            await handleJoin(event.id, joinUrl, false);
          }}
        >
          Join the waiting room
        </YellowButton>
      )}
      {!isNow && !isSoon && (
        <YellowButton onClick={() => handleJoin(event.id)}>Book Experience</YellowButton>
      )}
      {clbcPrompt}
    </>
  );

  if (mode === ExperienceMode.InPerson) {
    if (user?.welcomeVetted) {
      if (isMobile) {
        return (
          <Fragment>
            <MobileButtonRow>
              <YellowButton onClick={() => handleJoin(event.id)}>Book</YellowButton>
            </MobileButtonRow>
            {clbcPrompt}
          </Fragment>
        );
      } else {
        return (
          <Fragment>
            <ButtonRow>
              <YellowButton onClick={() => handleJoin(event.id)}>Book Experience</YellowButton>
            </ButtonRow>
            {clbcPrompt}
          </Fragment>
        );
      }
    } else {
      if (isMobile) {
        return (
          <Fragment>
            <MobileButtonRow>
              <YellowButton onClick={() => setScheduleWelcomeModalOpen(true)}>
                Book a Welcome
              </YellowButton>
              <ScheduleWelcomeModal />
            </MobileButtonRow>
            {clbcPrompt}
          </Fragment>
        );
      } else {
        return (
          <Fragment>
            <ButtonRow>
              <YellowButton onClick={() => setScheduleWelcomeModalOpen(true)}>
                Book a Welcome
              </YellowButton>
              <ScheduleWelcomeModal />
            </ButtonRow>
            {clbcPrompt}
          </Fragment>
        );
      }
    }
  } else {
    if (isMobile) {
      return <MobileButtonRow>{content}</MobileButtonRow>;
    } else {
      return <ButtonRow>{content}</ButtonRow>;
    }
  }
};

export default BookingButton;
